/**
 * <testimonial-carousel>
 * Lightweight scroll-snap carousel: native horizontal scrolling with prev/next
 * buttons and dot indicators kept in sync via IntersectionObserver.
 */
class TestimonialCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
    this.prevButton = this.querySelector('[data-carousel-prev]');
    this.nextButton = this.querySelector('[data-carousel-next]');
    this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));

    if (!this.track || this.slides.length === 0) return;

    this.prevButton?.addEventListener('click', () => this.scrollBySlide(-1));
    this.nextButton?.addEventListener('click', () => this.scrollBySlide(1));
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.scrollToIndex(index));
    });

    this.observer = new IntersectionObserver(this.handleIntersect.bind(this), {
      root: this.track,
      threshold: 0.6,
    });
    this.slides.forEach((slide) => this.observer.observe(slide));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  handleIntersect(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const index = this.slides.indexOf(entry.target);
      this.setActiveDot(index);
    });
  }

  setActiveDot(index) {
    this.dots.forEach((dot, dotIndex) => {
      dot.classList.toggle('is-active', dotIndex === index);
      dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false');
    });
  }

  scrollBySlide(direction) {
    const slide = this.slides[0];
    if (!slide) return;
    const amount = slide.getBoundingClientRect().width + this.getGap();
    this.track.scrollBy({ left: amount * direction, behavior: 'smooth' });
  }

  scrollToIndex(index) {
    const slide = this.slides[index];
    if (!slide) return;
    this.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
  }

  getGap() {
    const styles = window.getComputedStyle(this.track);
    return parseFloat(styles.columnGap || styles.gap || '0');
  }
}

if (!customElements.get('testimonial-carousel')) {
  customElements.define('testimonial-carousel', TestimonialCarousel);
}
